import { useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import clsx from 'clsx';
import { CircleCheck } from 'lucide-react';

import { glacierNewGradApiGetAnswersOptions } from '@/lib/heyapi/@tanstack/react-query.gen';

import { formatDateWithSlashes } from '@/lib/utils/dates';

import { CircleSideBar } from './CircleSideBar';

import { AgencyType } from '../newGradLayout';

import {
  getCurrentAndNextStep,
  getStepState,
  isStepCompleted,
  newGradSteps,
  getStepStyles,
  statusToLabel,
} from './utils';

export const SideBar = ({
  currentStep,
  agencyData,
  canNavigate,
  validateAgency,
}: {
  currentStep: number;
  agencyData: AgencyType;
  canNavigate: boolean;
  validateAgency: any;
}) => {
  const { pk, agency_id } = useParams();
  const { data } = useQuery({
    ...glacierNewGradApiGetAnswersOptions({
      path: {
        account_id: pk!,
      },
    }),
    enabled: !!pk,
  });

  const totalSteps = newGradSteps.length;

  const progress = (currentStep / totalSteps) * 100;
  const dashOffset = Math.max(0, 100 - progress);
  const { currStep, nextStep } = getCurrentAndNextStep(
    currentStep,
    newGradSteps
  );

  const isPaid = Boolean(data?.pages.payment?.paymentData?.paidAt);

  useEffect(() => {
    const state = data?.pages.yourInformation.policyContractState || null;
    const agencyId = agency_id || null;
    if (state || agencyId) {
      validateAgency.mutate({
        body: {
          agencyUuid: agencyId,
          practiceState: state,
        },
      });
    }
  }, [data, agency_id]);

  return (
    <>
      <div className="hidden w-full flex-col rounded-lg bg-gray-100 px-6 pb-8 pt-6 md:flex">
        {newGradSteps.map((step, idx) => {
          const stepState = getStepState(step.id, currentStep, data?.pages);
          const isPrevStepCompleted = isStepCompleted(step.id - 1, data?.pages);
          const isCompleted = isStepCompleted(step.id, data?.pages);

          const { circle, text } = getStepStyles(stepState, isCompleted);

          // If we have paid or are in a set we can't go back to, don't allow navigation via sidebar
          let url = `/newgrad${pk ? `/${pk}` : ''}/${step.id}/${agency_id || ''}`;
          if (!canNavigate) {
            url = '#';
          }

          return (
            <div key={step.id}>
              <Link
                className={`${isCompleted || isPrevStepCompleted ? '' : 'pointer-events-none'}`}
                to={url}
              >
                <div className="flex items-center">
                  <div
                    className={clsx(
                      'flex h-8 w-8 min-w-8 items-center justify-center rounded-full font-medium',
                      circle
                    )}
                  >
                    {isCompleted && stepState !== 'current' ? (
                      <CircleCheck size={32} color="#16A34A" strokeWidth={1} />
                    ) : (
                      step.id
                    )}
                  </div>
                  <span className={clsx('ml-3 text-base', text)}>
                    {step.title}
                  </span>
                </div>
              </Link>
              {idx < newGradSteps.length - 1 ? (
                <div className="my-[6px] ml-4 h-7 border-l border-[#C3CFD0]" />
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="flex w-full items-center justify-between rounded-lg bg-gray-100 p-4 md:hidden">
        <div>
          <h2 className="text-lg font-bold text-brand-700">
            {currStep?.title}
          </h2>
          {nextStep?.id ? (
            <p className="mt-1 text-base text-brand-500">
              Next: {nextStep?.title}
            </p>
          ) : null}
        </div>
        <div className="relative h-16 w-16 min-w-16">
          <CircleSideBar dashOffset={dashOffset} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-bold">
              {Math.min(currentStep, totalSteps)}
            </span>
            <span className="text-[#71717A]">/</span>
            <span className="text-[#71717A]">{totalSteps}</span>
          </div>
        </div>
      </div>
      {agencyData.name && (
        <>
          <div className="mt-6 flex flex-col gap-3 rounded-lg border border-brand-200 p-4">
            {isPaid ? (
              <>
                {data?.boundPolicyNumber && (
                  <div className="flex">
                    <div className="w-6/12 text-brand-500">Policy:</div>
                    <div className="w-6/12 font-semibold">
                      {data?.boundPolicyNumber}
                    </div>
                  </div>
                )}
                {data?.status && (
                  <div className="flex">
                    <div className="w-6/12 text-brand-500">Status:</div>
                    <div className="w-6/12 font-semibold">
                      {statusToLabel[data?.status]}
                    </div>
                  </div>
                )}
                {data?.effectiveDate && (
                  <div className="flex">
                    <div className="w-6/12 text-brand-500">Effective Date:</div>
                    <div className="w-6/12 font-semibold">
                      {formatDateWithSlashes(data?.effectiveDate)}
                    </div>
                  </div>
                )}
                <div className="flex">
                  <div className="w-3/12 text-brand-500">Agency:</div>
                  <div className="w-9/12 break-words font-semibold">
                    <p>
                      {agencyData.firstName} {agencyData.lastName}
                    </p>
                    <p>{agencyData.name}</p>
                    <p>{agencyData.email}</p>
                    <p>{agencyData.phone}</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex">
                  <div className="w-3/12 text-brand-500">Quote:</div>
                  <div className="w-9/12 font-semibold">
                    {data?.quoteNumber}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-3/12 text-brand-500">Agency:</div>
                  <div className="w-9/12 break-words font-semibold">
                    <p>
                      {agencyData.firstName} {agencyData.lastName}
                    </p>
                    <p>{agencyData.name}</p>
                    <p>{agencyData.email}</p>
                    <p>{agencyData.phone}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};
